import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Col,
  Descriptions,
  Form,
  Input,
  Layout,
  Modal,
  notification,
  Popconfirm,
  Row,
  Select,
  Tabs,
  Typography,
} from 'antd';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import INTERNAL_LINKS from '../../../enums/InternalLinks';
import { catchException } from '../../../utils/helpers/helpers';
import { retrieveForm } from '../../../api/module/forms';
import { CustomButton } from '../../../components';
import {
  createSectionForm,
  deleteSectionForm,
  getSectionForm,
  querySections,
  updateSectionForm,
} from '../../../api/module/section_form';
import SpinLoad from '../../../components/SpinLoad';
import { EditOutlined } from '@ant-design/icons';
import Section from './Section';

const { Content } = Layout;
const { Title } = Typography;

export default function FormQuestions({id}) {
  const [formSection] = Form.useForm();
  const [formInstance, setFormInstance] = useState(null);
  const [panes, setPanes] = useState([]);
  const [activeKey, setActiveKey] = useState(null);
  const [openModalSection, setOpenModalSection] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [loaderSections, setLoaderSections] = useState(false);
  const [flagCreateSection, setFlagCreateSection] = useState(true);
  const [sectionOptions, setSectionOptions] = useState([]);
  const [sectionId, setSectionId] = useState(undefined);
  const childRef = useRef(null);

  const breadcrumbItems = [
    {
      text: 'Formularios - Administración',
      to: `${INTERNAL_LINKS.ADMINISTRATION_FORMS}`,
    },
    {
      text: 'Formulario - Preguntas',
    },
  ];

  const joinTabPanes = (data) => {
    setPanes(panes.concat(data));
  };

  const tabAdd = (section) => {
    const tab = [
      {
        label: section.nombre,
        children: <Section sectionId={section.id} key={section.id} />,
        key: `seccion-${section.id}`,
      },
    ]
    joinTabPanes(tab);
    setActiveKey(`seccion-${section.id}`);
  };

  const tabRemove = async (targetKey) => {
    if (panes.length > 0) {
      try {
        await deleteSectionForm(targetKey.split("-")[1]);
        let newActiveKey = activeKey;
        let lastIndex = -1;
        panes.forEach((pane, i) => {
          if (pane.key === targetKey) {
            lastIndex = i - 1;
          }
        });
        const newPanes = panes.filter((pane) => pane.key !== targetKey);
        if (newPanes.length && newActiveKey === targetKey) {
          if (lastIndex >= 0) {
            newActiveKey = newPanes[lastIndex].key;
          } else {
            newActiveKey = newPanes[0].key;
          }
        }
        setPanes(newPanes);
        setActiveKey(newActiveKey);
        notification.success({
          message: "Eliminar Sección",
          description: "La sección fue eliminada exitosamente.",
        });
      } catch (error) {
        catchException(error);
      } finally {
        await fetchSections();
      }
    }
  };

  const tabOnEdit = async (targetKey, action) => {
    if (action === 'add') {
      setFlagCreateSection(true);
      setOpenModalSection(true);
    } else {
      setFlagCreateSection(false);
      setLoadingData(true);
      setOpenModalSection(true);
      let section = await getSectionForm(targetKey.split("-")[1]);
      formSection.setFieldsValue(section.data);
      setSectionId(section.data.id);
      setLoadingData(false);
    }
  };

  const tabOnChange = (newActiveKey) => {
    setActiveKey(newActiveKey);
    let sectionId = newActiveKey.split("-")[1]
    panes.find(s => s.key === newActiveKey).children = <Section sectionId={sectionId} formularioId={formInstance.id} key={crypto.randomUUID()} changeTab={tabOnChange} />;
  };

  const fetchForm = async () => {
    try {
      return await retrieveForm(id);
    } catch (error) {
      catchException(error);
    }
  };

  const fetchSections = async () => {
    try {
      let sections = await querySections({"filter[formulario]": formInstance.id}, 1, 25, '', false)
      setSectionOptions(arrangeListSections(sections.data.results));
      return sections.data.results;
    } catch(error) {
      catchException(error);
    }
  };

  const loadSections = async () => {
    setLoaderSections(true);
    if (formInstance) {
      const sections = await fetchSections();
      let data = [];
      sections.forEach(section => {
        data.push({
          label: section.nombre,
          children: <Section sectionId={section.id} formularioId={formInstance.id} key={section.id} changeTab={tabOnChange}/>,
          key: `seccion-${section.id}`,
        });
      });
      joinTabPanes(data);
      setActiveKey(data[0]?.key);
    }
    setLoaderSections(false);
  };

  const loadTabActive = (activeKey) => {
    panes.forEach((pane, i) => {
      if (pane.key === activeKey) {
        console.log(`section name: ${pane.label} and section id: ${activeKey}`);
      }
    })
  };

  const arrangeListSections = (sections) => {
    return sections.map(section => ({label: section.nombre, value: section.id}));
  }

  useEffect(() => {
    fetchForm().then(r => {setFormInstance(r.data)});
  }, []);

  useEffect(() => {
    loadSections().then(r => {});
  }, [formInstance]);

  const resetFormSection = () => {
    formSection.resetFields();
    setOpenModalSection(false);
    setSectionId(undefined);
  };

  const saveSection = async (values) => {
    setLoading(true);
    values.formulario = formInstance.id;
    try {
      if (flagCreateSection) {
        let section = await createSectionForm(values);
        tabAdd(section.data);
        notification.success({
          message: "Creación de Sección",
          description: `Se ha creado la sección "${values.nombre}" exitosamente.`,
          duration: 4,
        });
      } else {
        await updateSectionForm(sectionId, values);
        panes.forEach((pane, i) => {
          if (pane.key === `seccion-${sectionId}`) {
            pane.label = values.nombre
          }
        })
        notification.success({
          message: "Actualización de Sección",
          description: `Se ha actualizado la sección "${values.nombre}" exitosamente.`,
          duration: 4,
        });
      }
    }catch (error) {
      catchException(error);
    } finally {
      resetFormSection();
      await fetchSections();
      setLoading(false);
      setFlagCreateSection(true);
    }
  };

  return (
    <Content>
      <Row>
        <Col span={24}>
          <BreadcrumbContainer items={breadcrumbItems} />
        </Col>
        <Col span={24}>
          <Title className={'text-uppercase'} level={2}>
            Formulario - Preguntas
          </Title>
        </Col>
      </Row>
      <Descriptions
        column={3}
        bordered
        size="small"
      >
        <Descriptions.Item label="Proyecto:">
          {formInstance?.proyecto}
        </Descriptions.Item>
        <Descriptions.Item label="Código:">
          {formInstance?.codigo}
        </Descriptions.Item>
        <Descriptions.Item label="Formulario:">
          {formInstance?.nombre}
        </Descriptions.Item>
      </Descriptions>
      <br/>
      <Alert
        message="Haga click en el boton + para crear una sección del formulario."
        type="info"
        showIcon
      />
      <br/>
      <SpinLoad loading={loaderSections}>
        <Tabs
          type="editable-card"
          items={panes}
          onEdit={tabOnEdit}
          activeKey={activeKey}
          onChange={tabOnChange}
          //onTabClick={loadTabActive}
          removeIcon={<EditOutlined />}
        />
      </SpinLoad>
      <Modal
        title={(flagCreateSection) ? 'Creación de Sección' : 'Actualizar Sección'}
        open={openModalSection}
        onCancel={resetFormSection}
        footer={null}
        loading={loadingData}
        destroyOnClose
      >
        <Form
          form={formSection}
          layout="vertical"
          name="handleSection"
          onFinish={saveSection}
          preserve={false}
        >
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                label="Nombre de Sección"
                name="nombre"
                rules={[
                  { required: true, message: 'Por favor ingrese un nombre para la sección.' },
                  { min: 3, message: 'El nombre debe tener al menos 3 caracteres' },
                  { max: 30, message: 'El nombre no puede tener más de 30 caracteres' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label="Siguiente Sección"
                name="siguiente_section"
              >
                <Select
                  options={sectionOptions.filter( o => o.value !== sectionId)}
                  placeholder="Seleccione una sección..."
                />
              </Form.Item>
            </Col>
          </Row>
          <Row span={24}>
            <Col span={8}>
              <Form.Item>
                <CustomButton
                  type="primary"
                  htmlType="submit"
                  text={(flagCreateSection) ? 'Guardar' : 'Actualizar'}
                  loading={loading}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item>
                <CustomButton
                  type="default"
                  htmlType="button"
                  text="Volver"
                  onClick={resetFormSection}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Popconfirm
                title="Eliminar Sección"
                description="Está seguro de eliminar esta sección ?"
                onConfirm={() => {tabRemove(`seccion-${sectionId}`).then( r => resetFormSection() )}}
              >
                <CustomButton
                  type="danger"
                  htmlType="button"
                  text="Eliminar"
                />
              </Popconfirm>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Content>
  )
}
